import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useDiagramContext } from '../Context/DiagramContext';
import { saveDiagram } from '../../../services/diagramService';
import { closeDiagram, updateDiagramData, refreshTaxonomy } from '../store/diagramViewerSlice';
import ShowToast, { ToastTypeEnum } from '../../../common/components/ShowToast';
import { useBpmnInitialization } from './useBpmnInitialisation';
import { useDiagramEventListeners } from './useDiagramEventListner';
import { useDiagramBadges } from './useDiagramBadges';

export const useDiagramSave = () => {
  const dispatch = useDispatch();
  const {
    diagramData,
    bpmnModelerRef,
    containerRef,
    isEditPermission,
    selectedSubprocess,
    selectedCard,
    isProcessDiagram,
    lastSavedXmlRef,
    prevDiagramData,
    setPrevDiagramData,
    onDiagramSaved,
    onDiagramChange,
    setIsDiagramChanged,
    setSaveError,
  } = useDiagramContext();

  const { initializeBpmn } = useBpmnInitialization(isEditPermission, isProcessDiagram);
  const { attachEventListeners } = useDiagramEventListeners();
  const { updateDiagramBadges } = useDiagramBadges();

  const handleSaveDiagram = useCallback(async () => {
    if (!diagramData?.diagram?.id) {
      setSaveError('No diagram data available to save.');
      return;
    }

    try {
      let xmlToSave = diagramData.diagram.xml;
      let dataTosave = diagramData.diagram.data || {};

      if (bpmnModelerRef.current && isEditPermission) {
        const result = await bpmnModelerRef.current.saveXML({ format: true });
        if (result?.xml) {
          xmlToSave = result.xml;
        }
      }

      const formData = new FormData();
      formData.append('xmlData', new File([xmlToSave], 'diagram.xml', { type: 'text/xml' }));
      formData.append('data', JSON.stringify(dataTosave));

      await saveDiagram(diagramData.diagram.id, formData);

      if (location?.pathname?.includes('ideation-xplr')) {
        if (lastSavedXmlRef && 'current' in lastSavedXmlRef) {
          (lastSavedXmlRef as any).current = xmlToSave;
        }
        setPrevDiagramData?.(dataTosave);
      }

      dispatch(
        updateDiagramData({
          DiagramData: {
            ...diagramData,
            diagram: {
              ...diagramData.diagram,
              xml: xmlToSave,
              data: dataTosave,
            },
          },
        })
      );

      ShowToast({ message: 'BPMN Diagram saved successfully', type: ToastTypeEnum.Success });

      dispatch(refreshTaxonomy(true));

      if (selectedSubprocess?.processType === 'custom' || selectedCard?.processType === 'custom') {
        setIsDiagramChanged(false);
        if (onDiagramSaved) {
          onDiagramSaved(xmlToSave, dataTosave);
        }
      } else if (isProcessDiagram) {
        setIsDiagramChanged(false);
        if (onDiagramChange) {
          onDiagramChange(false);
        }
        if (onDiagramSaved) {
          onDiagramSaved(xmlToSave, dataTosave);
        }
      } else {
        setIsDiagramChanged(false);
        if (onDiagramSaved) {
          onDiagramSaved(xmlToSave, dataTosave);
        }
        dispatch(closeDiagram());
      }
    } catch (error: any) {
      console.error('Failed to save diagram:', error);
      setSaveError(error.message || 'Failed to save diagram. Please try again.');
    }
  }, [
    diagramData,
    bpmnModelerRef,
    containerRef,
    isEditPermission,
    selectedSubprocess,
    selectedCard,
    isProcessDiagram,
    dispatch,
    onDiagramSaved,
    onDiagramChange,
    setIsDiagramChanged,
    setSaveError,
    initializeBpmn,
    attachEventListeners,
    updateDiagramBadges,
    lastSavedXmlRef,
    prevDiagramData,
    setPrevDiagramData,
  ]);

  const handleDiscardChanges = useCallback(async () => {
    try {
      // Get the previous XML to restore
      const savedXml =
        lastSavedXmlRef && 'current' in lastSavedXmlRef
          ? (lastSavedXmlRef as any).current
          : undefined;

      const previousXml = location?.pathname?.includes('ideation-xplr')
        ? (typeof savedXml === 'string' ? savedXml : '') || diagramData?.diagram?.xml || ''
        : diagramData?.diagram?.xml || '';

      const previousData = (
        location?.pathname?.includes('ideation-xplr')
          ? prevDiagramData || diagramData?.diagram?.data || {}
          : diagramData?.diagram?.data || {}
      ) as {
        [key: string]: { name: string; description: string };
      };

      if (!previousXml || !containerRef.current) {
        return;
      }

      // Destroy existing instance
      if (bpmnModelerRef.current) {
        try {
          bpmnModelerRef.current.destroy();
        } catch (err) {
          console.warn('Error destroying modeler:', err);
        }
        bpmnModelerRef.current = undefined;
      }

      // Reinitialize with previous XML
      bpmnModelerRef.current = initializeBpmn(containerRef.current);
      await bpmnModelerRef.current.importXML(previousXml);

      // Update Redux state
      dispatch(
        updateDiagramData({
          DiagramData: {
            diagram: {
              id: diagramData?.diagram?.id || '',
              xml: previousXml,
              data: previousData,
            },
          },
        })
      );

      // Re-attach event listeners
      attachEventListeners();

      // Update badges
      setTimeout(() => {
        updateDiagramBadges();
      }, 0);

      setIsDiagramChanged(false);

      if (onDiagramChange && isProcessDiagram) {
        onDiagramChange(false);
      }

      ShowToast({ message: 'Changes discarded', type: ToastTypeEnum.Success });
    } catch (err) {
      console.error('Failed to discard changes:', err);
      ShowToast({ message: 'Failed to discard changes', type: ToastTypeEnum.Error });
    }
  }, [
    diagramData,
    bpmnModelerRef,
    containerRef,
    lastSavedXmlRef,
    prevDiagramData,
    isProcessDiagram,
    dispatch,
    onDiagramChange,
    setIsDiagramChanged,
    updateDiagramBadges,
    initializeBpmn,
    attachEventListeners,
  ]);

  return {
    handleSaveDiagram,
    handleDiscardChanges,
  };
};
